import React, { useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

export type PrescriptionMedicationDetailField =
  | 'active_ingredient'
  | 'concentration'
  | 'pharmaceutical_form'
  | 'quantity'
  | 'route';

export interface PrescriptionMedicationDetailItem {
  active_ingredient?: string | null;
  concentration?: string | null;
  pharmaceutical_form?: string | null;
  quantity?: string | null;
  route?: string | null;
}

export interface PrescriptionMedicationDetailsFieldsProps {
  item: PrescriptionMedicationDetailItem;
  formOptions: readonly string[];
  routeOptions?: readonly string[];
  onFieldChange: (field: PrescriptionMedicationDetailField, value: string) => void;
  onRouteChange?: (route: string) => void;
  showActiveIngredient?: boolean;
  showQuantity?: boolean;
  showRoute?: boolean;
}

const compactFieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
};

const dash = (value?: string | null) => {
  const text = String(value || '').trim();
  return text || '—';
};

export function formatMedicationDetailsSummary(
  item: PrescriptionMedicationDetailItem,
  options: {
    showActiveIngredient?: boolean;
    showQuantity?: boolean;
    showRoute?: boolean;
  } = {},
): string {
  const { showActiveIngredient = false, showQuantity = true, showRoute = false } = options;
  const parts: string[] = [];

  if (showActiveIngredient) {
    parts.push(`Princípio ativo: ${dash(item.active_ingredient)}`);
  }
  parts.push(`Conc.: ${dash(item.concentration)}`);
  parts.push(`Forma: ${dash(item.pharmaceutical_form)}`);
  if (showQuantity) {
    parts.push(`Qtd.: ${dash(item.quantity)}`);
  }
  if (showRoute) {
    parts.push(`Via: ${dash(item.route || 'Oral')}`);
  }

  return parts.join(' · ');
}

export const PrescriptionMedicationDetailsFields: React.FC<
  PrescriptionMedicationDetailsFieldsProps
> = ({
  item,
  formOptions,
  routeOptions = [],
  onFieldChange,
  onRouteChange,
  showActiveIngredient = false,
  showQuantity = true,
  showRoute = false,
}) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 0.75,
          mt: 0,
          flexWrap: 'wrap',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            flexWrap: 'wrap',
            gap: 1,
            alignItems: 'center',
            minWidth: 0,
          }}
        >
          {showActiveIngredient && (
            <TextField
              size="small"
              label="Princípio ativo"
              value={item.active_ingredient || ''}
              onChange={(event) => onFieldChange('active_ingredient', event.target.value)}
              sx={{ ...compactFieldSx, flex: '2 1 160px', minWidth: 140 }}
            />
          )}
          <TextField
            size="small"
            label="Conc."
            value={item.concentration || ''}
            onChange={(event) => onFieldChange('concentration', event.target.value)}
            placeholder="500 mg"
            sx={{ ...compactFieldSx, flex: '1 1 88px', minWidth: 80 }}
          />
          <FormControl size="small" sx={{ ...compactFieldSx, flex: '1 1 108px', minWidth: 100 }}>
            <InputLabel>Forma</InputLabel>
            <Select
              value={item.pharmaceutical_form || ''}
              label="Forma"
              onChange={(event) => onFieldChange('pharmaceutical_form', event.target.value)}
            >
              {formOptions.map((form) => (
                <MenuItem key={form} value={form}>{form}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {showQuantity && (
            <TextField
              size="small"
              label="Qtd."
              value={item.quantity || ''}
              onChange={(event) => onFieldChange('quantity', event.target.value)}
              placeholder="1 caixa"
              sx={{ ...compactFieldSx, flex: '1 1 88px', minWidth: 80 }}
            />
          )}
          {showRoute && (
            <FormControl size="small" sx={{ ...compactFieldSx, flex: '1 1 108px', minWidth: 100 }}>
              <InputLabel>Via</InputLabel>
              <Select
                value={item.route || 'Oral'}
                label="Via"
                onChange={(event) => {
                  const route = event.target.value;
                  if (onRouteChange) onRouteChange(route);
                  else onFieldChange('route', route);
                }}
              >
                {routeOptions.map((route) => (
                  <MenuItem key={route} value={route}>{route}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
        <Tooltip title="Concluir edição">
          <IconButton
            size="small"
            onClick={() => setEditing(false)}
            sx={{ color: '#0D9488', mt: 0.25 }}
            aria-label="Concluir edição dos detalhes"
          >
            <CheckOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mt: 0.25,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        sx={{
          flex: 1,
          color: '#64748B',
          fontSize: '0.8125rem',
          lineHeight: 1.3,
          minWidth: 0,
        }}
      >
        {formatMedicationDetailsSummary(item, { showActiveIngredient, showQuantity, showRoute })}
      </Typography>
      <Tooltip title="Editar detalhes">
        <IconButton
          size="small"
          onClick={() => setEditing(true)}
          sx={{ color: '#94A3B8', '&:hover': { color: '#0D9488' }, mt: -0.25 }}
          aria-label="Editar princípio ativo, concentração e forma"
        >
          <EditOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default PrescriptionMedicationDetailsFields;
